import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'models/constants/input.constants';
import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption, mapAsBody } from 'app/shared/util/request-util';
import { Transport } from '../models/transport.model';

export type EntityResponseType = HttpResponse<Transport>;
export type EntityArrayResponseType = HttpResponse<Transport[]>;

@Injectable({ providedIn: 'root' })
export class TransportService {
  protected resourceUrl: string;

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) {
    this.resourceUrl = this.applicationConfigService.getEndpointFor('api/transports');
  }

  create(transport: Transport): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transport);
    return this.http.post<Transport>(this.resourceUrl, copy, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(transport: Transport): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transport);
    return this.http.put<Transport>(`${this.resourceUrl}/${transport.id}`, copy, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(transport: Transport): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transport);
    return this.http.patch<Transport>(`${this.resourceUrl}/${transport.id}`, copy, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  public find(id: number): Observable<Transport> {
    return this.http
      .get<Transport>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(mapAsBody)
      .pipe(map((res: any) => new Transport(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<Transport[]>(this.resourceUrl, { params: options, observe: 'response' }).pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  protected convertDateFromClient(transport: Transport): Transport {
    return Object.assign({}, transport, {
      pickupDate: transport.pickupDate?.isValid() ? transport.pickupDate.format(DATE_FORMAT) : undefined,
      deliveryDate: transport.deliveryDate?.isValid() ? transport.deliveryDate.format(DATE_FORMAT) : undefined,
      creationDate: transport.creationDate?.isValid() ? transport.creationDate.toJSON() : undefined,
      quotationValidityDate: transport.quotationValidityDate?.isValid() ? transport.quotationValidityDate.toJSON() : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.pickupDate = res.body.pickupDate ? dayjs(res.body.pickupDate) : undefined;
      res.body.deliveryDate = res.body.deliveryDate ? dayjs(res.body.deliveryDate) : undefined;
      res.body.creationDate = res.body.creationDate ? dayjs(res.body.creationDate) : undefined;
      res.body.quotationValidityDate = res.body.quotationValidityDate ? dayjs(res.body.quotationValidityDate) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((transport: Transport) => {
        transport.pickupDate = transport.pickupDate ? dayjs(transport.pickupDate) : undefined;
        transport.deliveryDate = transport.deliveryDate ? dayjs(transport.deliveryDate) : undefined;
        transport.creationDate = transport.creationDate ? dayjs(transport.creationDate) : undefined;
        transport.quotationValidityDate = transport.quotationValidityDate ? dayjs(transport.quotationValidityDate) : undefined;
      });
    }
    return res;
  }
}
